import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from "@angular/core";
import { PromptsModel } from "@modules/messaging-prompts/domain/entities/prompts.model";
import { PromptsFacade } from "@modules/messaging-prompts/domain/+state/prompts.facade";
import { filter, take } from "rxjs";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { SegmentService } from "@main-services/segment.service";
import { Web3Facade } from "@modules/web3/+state/web3.facade";
import { Web3NavigationService } from "@modules/web3/services/web3.navigation";
import { Web3MigrationRoutes } from "@modules/web3/w3st-migration.module";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "prompts-banner",
	templateUrl: "./prompts.component.html",
	styleUrls: ["./prompts.component.scss"],
	providers: [Web3Facade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class PromptsComponent implements OnInit {
	private readonly promptsFacade = inject(PromptsFacade);
	private readonly web3Facade = inject(Web3Facade);

	@Input() public promptsData: PromptsModel[];

	constructor(
		private readonly portfolioFacade: PortfolioFacade,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly web3NavigationService: Web3NavigationService
	) {}

	public ngOnInit(): void {
		this.trackPromptViewEvent();
	}

	public onButtonClicked(target: string, eventPid?: string): void {
		switch (this.promptsData[0]?.event?.targetPath) {
			case "/anti-phishing-code":
				this.trackEvent("APC Set Up Code Tapped");
				this.promptsFacade.onTargetPathClicked(target, eventPid);
				break;
			case "/nt-migration":
				this.trackWeb3Event("Web3 Token Prompt Get Started Get Started Clicked");
				this.web3NavigationService.gotoWeb3ByUrl(Web3MigrationRoutes.autoMigrationIntro);
				break;
			default:
				this.promptsFacade.onTargetPathClicked(target, eventPid);
		}
	}

	public onDoItLaterClicked(): void {
		this.trackEvent("Web3 Token Prompt Do It Later Button Clicked");
		const dateTimeDoItLaterClick = new Date();
		localStorage.setItem("web3DoItLater", dateTimeDoItLaterClick.toString());
	}

	private trackPromptViewEvent(): void {
		switch (this.promptsData[0]?.event?.targetPath) {
			case "/anti-phishing-code":
				this.trackEvent("APC Dashboard Prompt Viewed");
				break;
			case "/nt-migration":
				this.trackWeb3Event("Web3 Token Prompt Seen");
				break;
			default:
				return;
		}
	}

	private trackEvent(name: string): void {
		this.portfolioFacade
			.getPortfolioWidgetValues$()
			.pipe(
				filter((data) => !data.isLoading),
				take(1)
			)
			.subscribe((data) => {
				this.segmentService.track({
					name,
					properties: {
						screen_it_happened_on: data.assetCount > 0 ? "home_trades" : "home_no_trades"
					}
				});
				this.rudderstackService.track({
					name,
					properties: {
						screen_it_happened_on: data.assetCount > 0 ? "home_trades" : "home_no_trades"
					}
				});
			});
	}

	private trackWeb3Event(name: string): void {
		this.web3Facade
			.getMergePhase()
			.pipe(take(1))
			.subscribe((phase) => {
				this.segmentService.track({
					name,
					properties: {
						screen_it_happened_on: "home",
						phase: phase.phase
					}
				});
				this.rudderstackService.track({
					name,
					properties: {
						screen_it_happened_on: "home",
						phase: phase.phase
					}
				});
			});
	}
}
